import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import { isRequired } from 'components/Questionnaires/validators';
import LocalisedMessage from 'components/Common/LocalisedMessage';

export const QuestionMultiCheckBoxField = ({ intl, blockId, question, className, onChange }) => {
  const [inputValues, setInputValues] = useState(question?.answer ?? question.default ?? []);
  const [focusedCheckbox, setFocusedCheckbox] = useState({});

  const onInputChange = (itemValue) => {
    const newValues = inputValues.includes(itemValue)
      ? inputValues.filter(value => value !== itemValue)
      : [...inputValues, itemValue];

    setInputValues(newValues);
    onChange(question, newValues);
  };

  const handleFocus = (itemValue) => {
    setFocusedCheckbox(prev => ({
      ...prev,
      [itemValue]: true,
    }));
  };

  const handleBlur = (itemValue) => {
    setFocusedCheckbox(prev => ({
      ...prev,
      [itemValue]: false,
    }));
  };

  const items = useMemo(() => {
    if (!question?.componentProps?.items) {
      return [];
    }

    if (typeof question.componentProps.items === 'string') {
      const itemsArray = intl.messages[question.componentProps.items] ?? [];
      return (typeof itemsArray === 'string') ? [] : itemsArray;
    }
    if (Array.isArray(question.componentProps.items)) {
      return question.componentProps.items;
    }
    return [];
  }, [question?.componentProps?.items, intl]);

  useEffect(() => {
    if (inputValues.length) {
      onChange(question, inputValues);
    }
  }, []);

  return (
    <div
      className={ `checkbox-container ${ className }` }
      id={ `${ blockId }-${ question.id }` }
      aria-required={ isRequired(question) }
      aria-describedby={ question.isValid === false ? (`${ blockId }-${ question.id }-error`) : undefined }
      aria-labelledby={ `${ blockId }-${ question.id }-lbl` }
    >
      {items.map(item => (
        <div
          className="ml-3"
          key={ `${ blockId }-${ question.id }-${ item.value }` }
        >
          <label className="form-checkbox" key={ item.value }>
            <input
              id={ `${ blockId }-${ question.id }-${ item.value }` }
              name={ `${ question.id }-${ item.value }` }
              className={ `${ className }${ !question.isValid ? ' error' : '' }` }
              type="checkbox"
              value={ item.value }
              checked={ inputValues.includes(item.value) }
              disabled={ question.disabled }
              onFocus={ () => handleFocus(item.value) }
              onBlur={ () => handleBlur(item.value) }
              onChange={ () => onInputChange(item.value) }
              aria-checked={ inputValues.includes(item.value) }
              aria-describedby={
                question.isValid === false
                  ? `${ blockId }-${ item.value }-error`
                  : undefined
              }
            />
            <i className={ `form-icon ${ focusedCheckbox[item.value] ? 'focus' : '' }` } />
            <span>
              <LocalisedMessage id={ item.label } />
            </span>
          </label>
        </div>
      ))}
    </div>
  );
};

QuestionMultiCheckBoxField.displayName = 'QuestionCheckBoxField';

QuestionMultiCheckBoxField.propTypes = {
  intl: PropTypes.shape().isRequired,
  blockId: PropTypes.string.isRequired,
  question: PropTypes.shape().isRequired,
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

QuestionMultiCheckBoxField.defaultProps = {
  className: '',
};

export default QuestionMultiCheckBoxField;
